import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS} from '@contentful/rich-text-types'
import {Button, CTABanner, type CTABannerProps} from '@primer/react-brand'
import {clsx} from 'clsx'

import type {PrimerComponentCtaBanner} from '../../../schemas/contentful/contentTypes/primerComponentCtaBanner'
import {getAnalyticsEvent} from '../../../lib/utils/analytics'
import {getStructuredImageSources, MAX_CONTENT_WIDTH} from '../../../lib/utils/images'

import {ContentfulAppStoreButtonGroup} from '../ContentfulAppStoreButtonGroup/ContentfulAppStoreButtonGroup'
import styles from './ContentfulCtaBanner.module.css'
import {getLinkHref} from '../../../lib/utils/links'

export type ContentfulCtaBannerProps = {
  component: PrimerComponentCtaBanner
  className?: string
  backgroundImage?: string
}

export function ContentfulCtaBanner({component, className, backgroundImage}: ContentfulCtaBannerProps) {
  const {colorMode, focus, image} = component.fields?.backgroundImage?.fields || {}
  const hasTrailingComponent = !!component.fields?.trailingComponent?.length
  const backgroundImageUrl = backgroundImage || image?.fields?.file?.url
  const colorModeAttributes = colorMode === 'dark' || colorMode === 'light' ? {'data-color-mode': colorMode} : {}

  let backgroundImageSrc: CTABannerProps['backgroundImageSrc'] = backgroundImageUrl
  if (backgroundImageUrl) {
    backgroundImageSrc = getStructuredImageSources(backgroundImageUrl, {maxWidth: MAX_CONTENT_WIDTH})
  }

  const {callToActionPrimary, callToActionPrimaryVariant, callToActionSecondary, heading} = component.fields

  return (
    <CTABanner
      className={clsx(backgroundImageUrl && styles.withImage, className)}
      align={component.fields.align}
      hasBorder={component.fields.hasBorder}
      hasShadow={component.fields.hasShadow}
      hasBackground={component.fields.hasBackground}
      backgroundColor={component.fields.backgroundColor}
      backgroundImageSrc={backgroundImageSrc}
      backgroundImagePosition={backgroundImageUrl && (focus || 'center')}
      {...colorModeAttributes}
    >
      <CTABanner.Heading as={component.fields.headingLevel} size={component.fields.headingSize || '2'}>
        {component.fields.heading}
      </CTABanner.Heading>
      {component.fields.description && (
        <CTABanner.Description variant={backgroundImageUrl ? 'default' : 'muted'}>
          {documentToReactComponents(component.fields.description, {
            renderNode: {
              [BLOCKS.PARAGRAPH]: (_, children) => children,
            },
          })}
        </CTABanner.Description>
      )}
      {hasTrailingComponent ? (
        <ContentfulAppStoreButtonGroup
          components={component.fields.trailingComponent}
          analyticsLabel="cta-banner"
          analyticsLocation={component.fields.heading}
        />
      ) : (
        <CTABanner.ButtonGroup>
          {callToActionPrimary ? (
            <Button
              as="a"
              href={getLinkHref(callToActionPrimary)}
              variant={callToActionPrimaryVariant || 'primary'}
              {...(callToActionPrimary?.fields.openInNewTab && {target: '_blank'})}
              {...getAnalyticsEvent(
                {
                  action: callToActionPrimary?.fields.text || 'Primary CTA',
                  tag: 'button',
                  location: heading,
                  context: 'CTAs',
                },
                {context: false},
              )}
            >
              {callToActionPrimary.fields.text}
            </Button>
          ) : (
            <></>
          )}

          {callToActionSecondary ? (
            <Button
              as="a"
              href={getLinkHref(callToActionSecondary)}
              {...(callToActionSecondary.fields.openInNewTab && {target: '_blank'})}
              {...getAnalyticsEvent(
                {
                  action: callToActionSecondary.fields.text || 'Secondary CTA',
                  tag: 'button',
                  location: heading,
                  context: 'CTAs',
                },
                {context: false},
              )}
              data-ref={`cta-banner-secondary-action-${callToActionSecondary.sys.id}`}
            >
              {callToActionSecondary.fields.text}
            </Button>
          ) : (
            <></>
          )}
        </CTABanner.ButtonGroup>
      )}
    </CTABanner>
  )
}
